
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from '@/components/ui/button';
import { Tag, Star, Clock, ExternalLink, Filter } from 'lucide-react';

const CoachDeals = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Mock data for deals
  const deals = [
    {
      id: 1,
      title: '25% Off Sports Equipment',
      company: 'Athletic Pro Store',
      category: 'equipment',
      discount: '25%',
      description: 'Get 25% off all training equipment including cones, balls, and practice gear.',
      expiresAt: '2024-02-15',
      rating: 4.8,
      reviews: 127,
      code: 'COACH25',
      link: 'https://athleticpro.com/coach-deals'
    },
    {
      id: 2,
      title: 'Free Team Uniforms Design',
      company: 'Custom Sports Gear',
      category: 'uniforms',
      discount: 'Free',
      description: 'Free design consultation and mock-up for team uniforms. Order minimum 15 pieces.',
      expiresAt: '2024-01-30',
      rating: 4.9,
      reviews: 89,
      code: 'DESIGN2024',
      link: 'https://customsportsgear.com/coach'
    },
    {
      id: 3,
      title: '$50 Off Field Rental',
      company: 'City Sports Complex',
      category: 'venues',
      discount: '$50',
      description: 'Save $50 on your next field rental. Valid for bookings of 4+ hours.',
      expiresAt: '2024-03-01',
      rating: 4.6,
      reviews: 234,
      code: 'FIELD50',
      link: 'https://citysports.com/rentals'
    },
    {
      id: 4,
      title: '30% Off Coaching Certification',
      company: 'Sports Academy Online',
      category: 'education',
      discount: '30%',
      description: 'Get certified in advanced coaching techniques. Online courses available.',
      expiresAt: '2024-04-15',
      rating: 4.7,
      reviews: 156,
      code: 'LEARN30',
      link: 'https://sportsacademy.com/coaching'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Deals' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'uniforms', label: 'Uniforms' },
    { value: 'venues', label: 'Venues' },
    { value: 'education', label: 'Education' }
  ];

  const filteredDeals = selectedCategory === 'all' 
    ? deals 
    : deals.filter(deal => deal.category === selectedCategory);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'equipment':
        return 'bg-blue-600';
      case 'uniforms':
        return 'bg-purple-600';
      case 'venues':
        return 'bg-green-600';
      case 'education':
        return 'bg-orange-600';
      default:
        return 'bg-gray-600';
    }
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  return (
    <div className="flex flex-col min-h-screen bg-black w-full">
      <div className="hidden md:block">
        <Header />
      </div>
      <div className="block md:hidden">
        <MobileHeader />
      </div>
      
      <main className="flex-grow pt-0 mt-24 py-6 md:py-12 w-full px-4 md:px-6 pb-20 md:pb-6">
        <div className="w-full max-w-6xl mx-auto">
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardHeader>
              <CardTitle className="text-2xl text-sports-green flex items-center gap-2">
                <Tag className="h-6 w-6" />
                Coach Deals & Discounts
              </CardTitle>
              <p className="text-gray-400">
                Exclusive deals and discounts for coaches and teams
              </p>
            </CardHeader>
            <CardContent>
              {/* Category Filter */}
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <Filter className="h-4 w-4 text-gray-400" />
                {categories.map((category) => (
                  <button
                    key={category.value}
                    onClick={() => setSelectedCategory(category.value)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedCategory === category.value
                        ? 'bg-sports-green text-black'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              {/* Deals Grid */}
              <div className="grid gap-6">
                {filteredDeals.map((deal) => (
                  <div key={deal.id} className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-semibold text-lg">{deal.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getCategoryColor(deal.category)}`}>
                            {deal.category.charAt(0).toUpperCase() + deal.category.slice(1)}
                          </span>
                          {isExpiringSoon(deal.expiresAt) && (
                            <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Expires Soon
                            </span>
                          )}
                        </div>
                        <p className="text-sports-green font-medium mb-1">{deal.company}</p>
                        <p className="text-gray-400 mb-3">{deal.description}</p>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-current" />
                            <span>{deal.rating}</span>
                            <span>({deal.reviews} reviews)</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            <span>Expires: {new Date(deal.expiresAt).toLocaleDateString()}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="bg-gray-700 px-3 py-2 rounded border border-gray-600">
                            <span className="text-gray-400 text-sm">Code: </span>
                            <span className="text-white font-mono">{deal.code}</span>
                          </div>
                          <Button 
                            className="bg-sports-green text-black hover:bg-green-600"
                            onClick={() => window.open(deal.link, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Claim Deal
                          </Button>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold text-sports-green">
                          {deal.discount}
                        </div>
                        <div className="text-sm text-gray-400">OFF</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredDeals.length === 0 && (
                <div className="text-center py-8">
                  <Tag className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No deals available in this category.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default CoachDeals;
